/**
 * <gw-thumb-carousel> — drives the PDP gallery's thumbnail strip and hero.
 *
 * Wraps the gallery region inside `sections/gw-product-main.liquid`: the theme's
 * `product-media-gallery` output (hero `slider-element` + `media-dots` strip)
 * plus the two Figma chevrons.
 *
 * WHY NOT REUSE THE THEME'S CAROUSELS — all three were measured, not assumed:
 *
 * 1. `<slider-element>` around the strip pages by the WRONG COUNT.
 *    `SliderElement.perPage` is `floor(clientWidth / itemOffset)`. The strip is
 *    612px wide and itemOffset is 156 (144 thumb + 12 gap), so perPage computes
 *    **3** — four thumbs span 612 only because the last one carries no trailing
 *    gap, while 4 * 156 = 624. Confirmed by building a real `slider-element`
 *    against the live strip: `perPage === 3`. Widening the scroller to 624 to
 *    force 4 would resize the thumbs, since the track is `calc(25% - 9px)` of
 *    the container — (624 - 36) / 4 = 147px, breaking the Figma 144px and the
 *    measured pixel match. The design's 4-up/3-up is not expressible through
 *    that getter without giving up the geometry.
 * 2. `is="previous-button"` / `is="next-button"` are CUSTOMIZED BUILT-INS, which
 *    Safari does not support. The theme only leans on them for hover flourish,
 *    so failing to upgrade costs it nothing — but these chevrons are the only
 *    way to page the strip, and there they would be permanently dead in Safari.
 *    (They also can only target a `slider-element`; the strip is a `media-dots`,
 *    which has no `slider:previous` / `slider:next` listener.)
 * 3. `<carousel-element>` is the Flickity wrapper: it rebuilds the DOM into
 *    viewport/slider wrappers, which would break both the exact strip geometry
 *    and the `media-dots` → hero contract.
 *
 * TWO REAL THEME BUGS this works around, both reproduced in a visible browser
 * with this asset blocked:
 *
 * a. The chevrons used to scroll 0px. Aimed at the hero slider via the theme
 *    buttons, `next()` scrolls `perPage * itemOffset`; the theme's 4px `gap-1`
 *    on `.product__media-list` pushed itemOffset (660) past the slider's own
 *    width (656), so perPage floored to ZERO. Fixed in `gw-product-main-styles`
 *    by zeroing that gap — the hero slider now reports perPage 1.
 * b. `theme.initWhenVisible` FIRES ITS CALLBACK TWICE. Despite the name it is a
 *    script loader: a 5000ms timer OR the first user interaction. The timer path
 *    runs the callback but never removes the interaction listeners, so the first
 *    click runs it a second time. Every `initWhenVisible` consumer is affected;
 *    for `SliderDots` it means thumbnail clicks are bound twice, and since
 *    `select()` is RELATIVE to `currentPage` — the duplicate call landing before
 *    the debounced scroll handler updates it — the hero overshoots. Measured:
 *    first click `select(3)` once → correct media 3; second click `select(5)`
 *    twice off a stale page → media 5 instead of 6.
 *
 * So this element owns the strip and the thumb→hero relationship: `aria-controls`
 * is removed from `media-dots` (the only thing `SliderDots` checks before
 * binding, so dropping it disables the double-bound handler) and re-pointed at
 * each thumbnail button to keep the a11y association. Selection is ABSOLUTE
 * (scroll to `index * itemOffset`) rather than relative, so a duplicate event
 * cannot compound into the wrong slide.
 *
 * The hero `slider-element` is left alone — it initialises itself and keeps its
 * own swipe, snap and `slider:change` behaviour.
 *
 * TWO AXES — the strip runs under the hero on mobile and beside it as a vertical
 * rail from 1024px up. Rather than duplicate the paging maths, every measurement
 * below goes through the axis-aware getters (`scrollPos`, `viewSize`, …) and the
 * axis itself is read off the CSS (`grid-auto-flow`), so the breakpoint lives in
 * one place — the stylesheet — and the ResizeObserver already re-runs the arrow
 * state when the layout flips. The hero slider stays horizontal at both.
 */
class GwThumbCarousel extends HTMLElement {
  connectedCallback() {
    if (this.bound) return;

    this.list = this.querySelector('.product__thumbnails-list');
    this.prev = this.querySelector('.gw-pm__thumb-arrow--prev');
    this.next = this.querySelector('.gw-pm__thumb-arrow--next');
    this.slider = this.querySelector('slider-element');
    if (!this.list || !this.prev || !this.next) return;

    this.bound = true;
    this.rtl = getComputedStyle(this).direction === 'rtl';

    this.takeOverThumbnails();

    this.prev.addEventListener('click', () => this.page(-1));
    this.next.addEventListener('click', () => this.page(1));

    this.onListScroll = this.throttle(() => this.updateArrows());
    this.list.addEventListener('scroll', this.onListScroll, { passive: true });

    if (this.slider) {
      this.onHeroScroll = this.throttle(() => this.syncActive());
      this.slider.addEventListener('scroll', this.onHeroScroll, { passive: true });
    }

    if (window.ResizeObserver) {
      this.observer = new ResizeObserver(() => this.updateArrows());
      this.observer.observe(this.list);
    }

    this.enableZoomFallback();
    this.enableHeroArrows();

    this.updateArrows();
    this.syncActive();
  }

  /**
   * TAP-TO-ZOOM IN SAFARI. The theme opens PhotoSwipe from the overlay
   * `<button is="media-lightbox-button">` on each hero slide — another
   * CUSTOMIZED BUILT-IN (see note 2 in the header), so in Safari the button
   * never upgrades, its constructor-bound click handler never binds, and the
   * photo simply does not open. Desktop and iOS alike, which is exactly what
   * the client reported.
   *
   * `MediaGallery` itself is a plain autonomous element and works everywhere;
   * only the messenger is broken. So delegate the click here and re-send the
   * `lightbox:open` event the button would have sent. The guard is the button's
   * own constructor: where the built-in DID upgrade (Chrome, Firefox) the
   * element's handler already fires and this stands down, so the lightbox never
   * opens twice.
   */
  enableZoomFallback() {
    if (!this.slider) return;

    this.onZoomClick = (event) => {
      const button = event.target.closest('button[is="media-lightbox-button"], button[is="media-hover-button"]');
      if (!button || button.constructor !== HTMLButtonElement) return;

      const media = button.closest('[data-media-id]');
      const items = this.slider.itemsToShow || [...this.slider.querySelectorAll('[data-media-id]')];
      const index = media ? items.indexOf(media) : -1;

      button.dispatchEvent(new CustomEvent('lightbox:open', {
        bubbles: true,
        detail: { index }
      }));
    };

    this.slider.addEventListener('click', this.onZoomClick);
  }

  /**
   * Prev/next over the hero. Rendered beside the thumb chevrons, then moved
   * into the slider's own `relative` wrapper so they centre on the hero at
   * both breakpoints. Absolute selection, same as a thumb click.
   */
  enableHeroArrows() {
    this.heroPrev = this.querySelector('.gw-pm__hero-arrow--prev');
    this.heroNext = this.querySelector('.gw-pm__hero-arrow--next');
    if (!this.slider || !this.heroPrev || !this.heroNext) return;

    this.slider.parentElement.append(this.heroPrev, this.heroNext);
    this.heroPrev.addEventListener('click', () => this.selectMedia(this.currentIndex - 1));
    this.heroNext.addEventListener('click', () => this.selectMedia(this.currentIndex + 1));
  }

  updateHeroArrows(index = this.currentIndex) {
    if (!this.heroPrev) return;
    this.heroPrev.disabled = index <= 0;
    this.heroNext.disabled = index >= this.thumbs.length - 1;
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.list?.removeEventListener('scroll', this.onListScroll);
    this.slider?.removeEventListener('scroll', this.onHeroScroll);
    this.slider?.removeEventListener('click', this.onZoomClick);
  }

  /** rAF-coalesced callback. */
  throttle(fn) {
    let frame = null;
    return () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        fn();
      });
    };
  }

  get behavior() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }

  /** Stop `SliderDots` binding, then own the thumb→hero click ourselves. */
  takeOverThumbnails() {
    const dots = this.querySelector('media-dots');
    if (dots) dots.removeAttribute('aria-controls');

    this.thumbs.forEach((thumb, index) => {
      if (this.slider) thumb.setAttribute('aria-controls', this.slider.id);
      thumb.addEventListener('click', (event) => {
        event.preventDefault();
        this.selectMedia(index);
      });
    });
  }

  get thumbs() {
    return Array.from(this.list.children).filter((el) => !el.hidden);
  }

  get visibleThumbs() {
    return this.thumbs.filter((el) => el.clientWidth > 0);
  }

  /** Vertical rail (desktop) vs strip under the hero (mobile), per the CSS. */
  get vertical() {
    return !getComputedStyle(this.list).gridAutoFlow.includes('column');
  }

  get scrollPos() {
    return this.vertical ? this.list.scrollTop : Math.abs(this.list.scrollLeft);
  }

  get viewSize() {
    return this.vertical ? this.list.clientHeight : this.list.clientWidth;
  }

  get trackSize() {
    return this.vertical ? this.list.scrollHeight : this.list.scrollWidth;
  }

  /** Leading edge to leading edge: thumb size along the axis + gap. */
  get itemOffset() {
    const thumbs = this.visibleThumbs;
    if (thumbs.length < 2) return 0;
    return this.vertical
      ? Math.abs(thumbs[1].offsetTop - thumbs[0].offsetTop)
      : Math.abs(thumbs[1].offsetLeft - thumbs[0].offsetLeft);
  }

  /**
   * One full view. Adding one gap back to the visible extent makes it divide
   * evenly by itemOffset, yielding the number of thumbs on show without
   * hardcoding it — horizontally that follows `--gw-pm-thumb-count`, and on the
   * rail it follows however many rows the hero's height leaves room for.
   */
  get pageSize() {
    const offset = this.itemOffset;
    if (!offset) return this.viewSize;
    const style = getComputedStyle(this.list);
    const gap = parseFloat(this.vertical ? style.rowGap : style.columnGap) || 0;
    const perView = Math.max(1, Math.round((this.viewSize + gap) / offset));
    return perView * offset;
  }

  get maxScroll() {
    return this.trackSize - this.viewSize;
  }

  get scrollable() {
    return this.maxScroll > 1;
  }

  page(direction) {
    if (!this.scrollable) return;
    const delta = direction * this.pageSize;
    // scrollBy resolves the RTL sign itself; RTL never applies to the rail.
    this.list.scrollBy(
      this.vertical ? { top: delta, behavior: this.behavior } : { left: delta, behavior: this.behavior }
    );
  }

  /** Absolute, so a repeated event cannot compound into the wrong slide. */
  selectMedia(index) {
    if (!this.slider) return;
    index = Math.max(0, Math.min(index, this.thumbs.length - 1));
    const offset = this.slider.itemOffset || this.slider.clientWidth;
    this.slider.scrollTo({ left: index * offset * (this.rtl ? -1 : 1), behavior: this.behavior });
    this.markActive(index);
  }

  /** Which hero slide is showing, derived from scroll position. */
  get currentIndex() {
    if (!this.slider) return 0;
    const offset = this.slider.itemOffset || this.slider.clientWidth;
    if (!offset) return 0;
    return Math.round(Math.abs(this.slider.scrollLeft) / offset);
  }

  syncActive() {
    this.markActive(this.currentIndex);
  }

  markActive(index) {
    const thumbs = this.thumbs;
    thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', i === index ? 'true' : 'false'));
    this.updateHeroArrows(index);
    this.revealThumb(thumbs[index]);
  }

  /**
   * Keep the active thumb visible, but only when it has actually scrolled out of
   * view — the theme's `SliderDots.transitionTo` re-CENTRES it on every hero
   * change, which would yank the strip back after the user paged it.
   */
  revealThumb(thumb) {
    if (!thumb || !this.scrollable) return;
    const vertical = this.vertical;
    const position = this.scrollPos;
    const start = vertical ? thumb.offsetTop : thumb.offsetLeft;
    const end = start + (vertical ? thumb.clientHeight : thumb.clientWidth);

    let target = null;
    if (start < position) target = start;
    else if (end > position + this.viewSize) target = end - this.viewSize;
    if (target === null) return;

    const clamped = Math.max(0, Math.min(target, this.maxScroll));
    this.list.scrollTo(
      vertical
        ? { top: clamped, behavior: this.behavior }
        : { left: clamped * (this.rtl ? -1 : 1), behavior: this.behavior }
    );
  }

  updateArrows() {
    const position = this.scrollPos;
    const scrollable = this.scrollable;

    // Nothing to page through (fewer media than fill a view). The design always
    // draws both chevrons, but a permanently dead pair reads as broken, and the
    // chevron gutter is padding on the strip — hiding them shifts no layout.
    this.prev.hidden = !scrollable;
    this.next.hidden = !scrollable;

    this.prev.disabled = position <= 1;
    this.next.disabled = position >= this.maxScroll - 1;
  }
}

if (!customElements.get('gw-thumb-carousel')) {
  customElements.define('gw-thumb-carousel', GwThumbCarousel);
}
